import React from 'react';
import { SanityPrice } from 'src/types/sanity/types.sanity.product';
import { useEnvironment } from 'src/components/hooks';
import { Stack, Box, Typography, useTheme } from '@mui/material';

interface PricesProps {
  prices: SanityPrice[];
  get: string | null;
  set: (value: string | null) => void;
}

const Prices = ({ prices, get, set }: PricesProps) => {
  const theme = useTheme();

  if (!prices || prices.length === 0) return null;

  const handleSelect = (priceId: string) => {
    set(priceId);
  };

  const checkMark = (
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
      <circle
        cx="13"
        cy="13"
        r="13"
        // fill="#BDDC04"
        fill={theme.palette.primary.main}
      />
      <path
        d="M6 13.1852L11.5676 18L20 8"
        stroke={theme.palette.primary.contrastText}
        stroke-width="2.5"
      />
    </svg>
  );

  const circle = (
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
      <circle cx="13" cy="13" r="12.5" stroke="#9E9E9E" />
    </svg>
  );

  /********************************************************************************
   * * FUNCTION: GET PRICE ID
   * ------------------------------------------------------------------------------
   * The purpose of this function is to get the price ID based on the environment.
   * *****************************************************************************/
  // * Determine the dev environment using the useEnvironment hook
  const { useDev } = useEnvironment();
  // * Find the price id based on the environment
  const getPriceId = (price: SanityPrice) => {
    let priceId: string;
    const environment = useDev ? 'development' : 'production';
    priceId = price.priceId.find(price => price.environment === environment)?.priceId ?? '';
    return priceId;
  };

  return (
    <Stack spacing={2}>
      <Typography variant={'h6'}>EARLY ACCESS PRICING</Typography>
      {prices.map((price, index) => {
        // Get the correct price ID based on the environment
        const priceId = getPriceId(price);

        // Check which is selected
        const selected = get === priceId;

        return (
          <Box
            display={'flex'}
            gap={2}
            justifyContent={'space-between'}
            key={index}
            onClick={() => handleSelect(priceId)}
            sx={{
              padding: 2,
              border: selected ? `1px solid ${theme.palette.primary.main}` : '1px solid grey',
              borderRadius: '5px',
              bgcolor: selected ? 'primary.light' : '#EEEFE6',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              '&:hover': {
                bgcolor: 'primary.light',
              },
            }}
          >
            <Box>{selected ? checkMark : circle}</Box>

            <Box display={'flex'} width={'100%'}>
              <Stack width={'100%'}>
                <Typography variant="body1" gutterBottom>{price.priceTitle}</Typography>
                {price.priceSubTitle ?? (
                  <>
                  <Typography variant={'caption'}>{price.priceSubTitle}</Typography>
                  </>
                )}
              </Stack>

              <Stack alignItems={'flex-end'}>
                <Box display={'flex'} gap={1}>
                  {price.anchorPrice && (
                    <Typography
                      variant="body2"
                      sx={{ color: 'grey', textDecoration: 'line-through' }}
                      gutterBottom
                    >
                      ${price.anchorPrice.toFixed(0)}
                    </Typography>
                  )}
                  <Typography variant="body2" gutterBottom>${price.price.toFixed(0)}</Typography>
                </Box>
                <Typography variant={'caption'}>
                  ${(price.price / 28).toFixed(2)}/serving
                </Typography>
              </Stack>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default Prices;
